import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';

const DEFAULT_COUNTRY_CODE = '+255'; // Tanzania

// Country codes for a more comprehensive set of countries
const countryCodes = [
  { code: '+255', country: 'Tanzania' },
  { code: '+254', country: 'Kenya' },
  { code: '+256', country: 'Uganda' },
  { code: '+250', country: 'Rwanda' },
  { code: '+257', country: 'Burundi' },
  { code: '+27', country: 'South Africa' },
  { code: '+1', country: 'USA/Canada' },
  { code: '+44', country: 'United Kingdom' },
  { code: '+33', country: 'France' },
  { code: '+49', country: 'Germany' },
  { code: '+91', country: 'India' },
  { code: '+86', country: 'China' },
  { code: '+81', country: 'Japan' },
  { code: '+61', country: 'Australia' },
  { code: '+39', country: 'Italy' },
  { code: '+34', country: 'Spain' },
  { code: '+351', country: 'Portugal' },
  { code: '+7', country: 'Russia' },
  { code: '+90', country: 'Turkey' },
  { code: '+966', country: 'Saudi Arabia' },
  { code: '+20', country: 'Egypt' },
  { code: '+212', country: 'Morocco' },
  { code: '+213', country: 'Algeria' },
  { code: '+234', country: 'Nigeria' },
  { code: '+62', country: 'Indonesia' },
  { code: '+63', country: 'Philippines' },
  { code: '+82', country: 'South Korea' },
  { code: '+380', country: 'Ukraine' },
  { code: '+48', country: 'Poland' },
  { code: '+46', country: 'Sweden' },
  { code: '+47', country: 'Norway' },
  { code: '+45', country: 'Denmark' },
  { code: '+358', country: 'Finland' },
  { code: '+43', country: 'Austria' },
  { code: '+41', country: 'Switzerland' },
  { code: '+31', country: 'Netherlands' },
  { code: '+32', country: 'Belgium' },
  { code: '+52', country: 'Mexico' },
  { code: '+55', country: 'Brazil' },
  { code: '+54', country: 'Argentina' },
  { code: '+56', country: 'Chile' },
  { code: '+57', country: 'Colombia' },
  { code: '+51', country: 'Peru' },
  { code: '+64', country: 'New Zealand' },
];

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  error?: string;
  placeholder?: string;
  disabled?: boolean;
}

export const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  required = false,
  error,
  placeholder = 'Phone number',
  disabled = false,
}) => {
  const [selectedCountryCode, setSelectedCountryCode] =
    useState(DEFAULT_COUNTRY_CODE);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const countryDropdownRef = useRef<HTMLDivElement>(null);
  const [phone, setPhone] = useState('');

  // Split value into code and phone if value changes externally
  useEffect(() => {
    const match = countryCodes.find(c => value.startsWith(c.code));
    if (match) {
      setSelectedCountryCode(match.code);
      setPhone(value.replace(match.code, ''));
    } else {
      setPhone(value);
    }
  }, [value]);

  // Auto-detect country on mount
  useEffect(() => {
    const detectUserCountry = async () => {
      try {
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        if (data.country_calling_code) {
          const detectedCountry = countryCodes.find(
            country => country.code === data.country_calling_code
          );
          if (detectedCountry) {
            setSelectedCountryCode(detectedCountry.code);
            return;
          }
        }
        if (data.country_name) {
          const detectedCountry = countryCodes.find(
            country =>
              country.country.toLowerCase() === data.country_name.toLowerCase()
          );
          if (detectedCountry) {
            setSelectedCountryCode(detectedCountry.code);
            return;
          }
        }
        setSelectedCountryCode(DEFAULT_COUNTRY_CODE);
      } catch {
        setSelectedCountryCode(DEFAULT_COUNTRY_CODE);
      }
    };
    detectUserCountry();
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target as Node)
      ) {
        setShowCountryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle phone input change
  const handlePhoneChange = (val: string) => {
    const clean = val.replace(/[^\d]/g, '');
    setPhone(clean);
    onChange(selectedCountryCode + clean);
  };

  // Handle country code change
  const handleCountryChange = (code: string) => {
    setSelectedCountryCode(code);
    setShowCountryDropdown(false);
    onChange(code + phone);
  };

  return (
    <div className="relative w-full">
      <input
        type="tel"
        value={phone}
        onChange={e => handlePhoneChange(e.target.value)}
        className={`w-full pl-20 pr-3 py-2.5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all duration-300 text-sm ${
          error ? 'border-red-500 focus:ring-red-500' : 'border-gray-200'
        }`}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
      />
      {/* Country Code Dropdown - Inside Input */}
      <div
        className="absolute left-0.5 top-0.5 bottom-0.5 w-16"
        ref={countryDropdownRef}
      >
        <button
          type="button"
          onClick={() => setShowCountryDropdown(!showCountryDropdown)}
          className="w-full h-full flex items-center justify-center gap-1 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-300 transition-colors rounded-l-xl"
          tabIndex={-1}
          disabled={disabled}
        >
          <span>{selectedCountryCode}</span>
          <ChevronDown
            className={`w-3 h-3 text-gray-500 transition-transform ${showCountryDropdown ? 'rotate-180' : ''}`}
          />
        </button>
        {/* Country Dropdown */}
        {showCountryDropdown && (
          <div className="absolute top-full mt-2 left-0 z-[9999] w-56 bg-white border border-gray-200 rounded-xl shadow-xl max-h-48 overflow-y-auto">
            {countryCodes.map(country => (
              <button
                key={country.code}
                type="button"
                onClick={() => handleCountryChange(country.code)}
                className={`w-full flex items-center gap-1 px-2 py-2 text-left hover:bg-gray-50 transition-colors text-xs ${
                  selectedCountryCode === country.code
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-700'
                }`}
              >
                <div className="flex-1">
                  <div className="font-medium text-xs">{country.country}</div>
                  <div className="text-xs text-gray-500">{country.code}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
};
